/**
 * taulukko.tsx — lajittelu ja haku yhdellä mekanismilla jokaiseen taulukkoon.
 *
 * Erä 22 (Toni 9.9.2026), A3–A7: «31 riviä on liikaa selattavaksi ja liian
 * vähän hakukentän ansaitsemiseksi — kunnes rivejä on 300. Tee lajittelu ja
 * haku yhdellä mekanismilla joka toimii jokaisessa taulukossa, älä neljää
 * erillistä.» Henkilöstössä oli jo oma toteutus (haku + lajiteltavat
 * otsikot); tämä on se sama nostettuna jaetuksi, ja Henkilöstö käyttää nyt
 * tätä.
 *
 * Säännöt jotka olivat Henkilöstössä ja pätevät kaikkialla:
 *  - Merkkijonot vertaillaan suomeksi (localeCompare "fi"), luvut lukuina.
 *  - Tyhjä (null/undefined/"") on aina VIIMEISENÄ kumpaankin suuntaan —
 *    tyhjä ei ole pienin arvo vaan puuttuva.
 *  - Tasapelin ratkaisee toissijainen avain (oletusavain), jotta järjestys
 *    on vakaa ja sama joka latauksella.
 *  - Haku osuu useaan kenttään kerralla (nimi, tunnus, nimike…), kirjainkoko
 *    ei merkitse.
 *
 * A7: lajittelu ja haku menevät osoitteeseen (`j`, `s`, `q`) kun näkymä antaa
 * osoitehaku-propit — F5 palauttaa saman listan ja linkin voi lähettää.
 * Mekanismi on `osoitehaku.ts`, sama kuin Hubin luvuilla.
 */
import { useState, type ReactNode } from "react";
import { lueHaku, useOsoiteHaku } from "./osoitehaku";
import { t } from "./kieli";

export type Suunta = 1 | -1;
type Arvo = string | number | boolean | null | undefined;

export interface TaulukkoAsetukset<T, A extends Record<string, (r: T) => Arvo>> {
  /** Lajitteluavaimet: avain → arvo rivistä. Avainten joukko päätellään tästä. */
  avaimet: A;
  /** Hakukentät: mihin merkkijonoihin haku osuu. */
  hakukentat?: (r: T) => (string | null | undefined)[];
  oletus: keyof A & string;
  oletusSuunta?: Suunta;
  /** Tasapelin ratkaisija, jos ei oletusavain nousevana (Työkohteet: tila, sitten alkaa uusin ensin). */
  toissijainen?: { avain: keyof A & string; suunta: Suunta };
  /** Avaimet joissa TYHJÄ ON ENSIMMÄISENÄ. Toni 10.9.2026: eräpäiväsarakkeessa tyhjä ei
   *  tarkoita «kaukana tulevaisuudessa» vaan «emme tiedä saako tätä käyttää» — laite jonka
   *  katsastuksesta ei ole tietoa on mahdollisesti kiireellisin, ja pohjalle hautautuneena
   *  sitä ei löydä kukaan. Nimissä tyhjä on yhä viimeisenä. */
  tyhjaEnsin?: (keyof A & string)[];
  /** Yksikkö selitteeseen («4 / 31 laitetta»). */
  yksikko?: string;
  /** A7: osoitteen hakuosa ja sen asetin — jos annetaan, tila kulkee osoitteessa. */
  osoiteHaku?: string;
  asetaOsoiteHaku?: (haku: string) => void;
}

export interface Taulukko<T, K extends string> {
  rivit: T[];
  haku: string;
  setHaku: (s: string) => void;
  jarj: { avain: K; suunta: Suunta };
  /** Lajiteltava sarakeotsikko: painike, jossa nuoli kertoo suunnan. */
  Otsikko: (avain: K, teksti: ReactNode) => ReactNode;
  /** Hakukenttä valmiina. */
  Haku: (asetukset?: { placeholder?: string; koko?: number }) => ReactNode;
  /** Kuinka monta riviä haku pudotti. */
  piilotettu: number;
}

const tyhja = (v: Arvo): boolean => v === null || v === undefined || v === "";

function vertaa(x: Arvo, y: Arvo): number {
  if (tyhja(x) && tyhja(y)) return 0;
  if (tyhja(x)) return 1;    // tyhjä viimeiseksi suunnasta riippumatta (kerrotaan ei suunnalla)
  if (tyhja(y)) return -1;
  if (typeof x === "number" && typeof y === "number") return x - y;
  if (typeof x === "boolean" && typeof y === "boolean") return (x ? 1 : 0) - (y ? 1 : 0);
  return String(x).localeCompare(String(y), "fi", { numeric: true, sensitivity: "base" });
}

export function useTaulukko<T, A extends Record<string, (r: T) => Arvo>>(
  rivit: T[] | null | undefined,
  a: TaulukkoAsetukset<T, A>,
): Taulukko<T, keyof A & string> {
  type K = keyof A & string;
  // Alustus osoitteesta useState-alustajassa (osoitehaku.ts: mountissa osoite on totuus).
  const alku = lueHaku(a.osoiteHaku);
  const [jarj, setJarj] = useState<{ avain: K; suunta: Suunta }>(() => {
    const j = alku.get("j"); const s = alku.get("s");
    return { avain: (j && j in a.avaimet ? j : a.oletus) as K, suunta: s === "l" ? -1 : s === "n" ? 1 : (a.oletusSuunta ?? 1) };
  });
  const [haku, setHaku] = useState(() => alku.get("q") ?? "");

  // A7: osoite ↔ tila. Ilman propseja hook on inertti (useOsoiteHaku sietää undefinedin).
  useOsoiteHaku(a.osoiteHaku, a.asetaOsoiteHaku,
    { j: jarj.avain === a.oletus ? "" : jarj.avain, s: jarj.suunta === (a.oletusSuunta ?? 1) ? "" : (jarj.suunta === 1 ? "n" : "l"), q: haku },
    (p) => {
      const j = p.get("j"); const s = p.get("s"); const q = p.get("q") ?? "";
      const avain = (j && j in a.avaimet ? j : a.oletus) as K;
      const suunta: Suunta = s === "l" ? -1 : s === "n" ? 1 : (a.oletusSuunta ?? 1);
      setJarj((v) => (v.avain === avain && v.suunta === suunta ? v : { avain, suunta }));
      setHaku((v) => (v === q ? v : q));
    });

  const sana = haku.trim().toLowerCase();
  const osuu = (r: T): boolean =>
    !sana || (a.hakukentat ? a.hakukentat(r) : Object.values(a.avaimet).map((f) => (f as (r: T) => Arvo)(r)))
      .some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(sana));
  const kaikki = rivit ?? [];
  const suodatetut = kaikki.filter(osuu);
  // Avain on aina joukosta (alustus ja osoite tarkistavat `in a.avaimet`); tyyppi ei sitä tiedä.
  const f = (a.avaimet[jarj.avain] ?? a.avaimet[a.oletus]) as (r: T) => Arvo;
  const toinen = a.toissijainen ?? { avain: a.oletus, suunta: 1 as Suunta };
  const g = (a.avaimet[toinen.avain] ?? a.avaimet[a.oletus]) as (r: T) => Arvo;
  const tyhjaEnsin = (k: K) => (a.tyhjaEnsin ?? []).includes(k);
  const jarjesta = (k: K, h: (r: T) => Arvo, suunta: Suunta) => (x: T, y: T): number => {
    const vx = h(x), vy = h(y);
    // Tyhjyys ei kerry suunnalla: tyhjä on tila, ei arvon ääripää. Oletus viimeiseksi,
    // eräpäivissä (tyhjaEnsin) ensimmäiseksi.
    if (tyhja(vx) !== tyhja(vy)) return (tyhja(vx) ? 1 : -1) * (tyhjaEnsin(k) ? -1 : 1);
    return vertaa(vx, vy) * suunta;
  };
  const ensisijainen = jarjesta(jarj.avain, f, jarj.suunta);
  const toissijainen = jarjesta(toinen.avain as K, g, toinen.suunta);
  const jarjestetyt = [...suodatetut].sort((x, y) => ensisijainen(x, y) || toissijainen(x, y));

  const Otsikko = (avain: K, teksti: ReactNode): ReactNode => (
    <button type="button" className="lajittele"
            aria-sort={jarj.avain === avain ? (jarj.suunta === 1 ? "ascending" : "descending") : "none"}
            onClick={() => setJarj((j) => ({ avain, suunta: j.avain === avain && j.suunta === 1 ? -1 : 1 }))}>
      {teksti}
      <span className="lajittele__nuoli">{jarj.avain === avain ? (jarj.suunta === 1 ? "▲" : "▼") : ""}</span>
    </button>
  );
  /* A7 (Toni 10.9.): kun haku ja lajittelu kulkevat osoitteessa, linkki kantaa suodattimen
     mukanaan — silloin näkymän on sanottava ääneen mikä suodatin on päällä, muuten
     vastaanottaja lukee osajoukkoa kokonaisuutena. Sama vikamuoto kuin nolla joka
     näyttää tulokselta. Selite piirtyy hakukentän viereen aina kun haku on päällä tai
     järjestys ei ole oletus. */
  const Haku = (asetukset?: { placeholder?: string; koko?: number }): ReactNode => {
    const oletusSuunta = a.oletusSuunta ?? 1;
    const eiOletus = jarj.avain !== a.oletus || jarj.suunta !== oletusSuunta;
    const yks = a.yksikko ?? t("pu.luku.rivia");
    return (
      <>
        <input type="search" className="taulukko__haku" value={haku} size={asetukset?.koko ?? 28}
               placeholder={asetukset?.placeholder ?? t("muu.taulukko.hae")} aria-label={t("muu.taulukko.hae_aria")}
               onChange={(e) => setHaku(e.target.value)} />
        {(sana || eiOletus) && (
          <span className="taulukko__selite" role="status" data-haku={sana} data-nakyvissa={suodatetut.length} data-kaikki={kaikki.length}>
            {sana && <>{t("muu.taulukko.haku")} «{haku.trim()}» · <b>{suodatetut.length} / {kaikki.length}</b> {yks}</>}
            {sana && eiOletus && " · "}
            {eiOletus && <>{t("muu.taulukko.jarjestys")} {jarj.avain} {jarj.suunta === 1 ? "▲" : "▼"}</>}
            {" "}<button type="button" className="nappi nappi--kevyt" onClick={() => { setHaku(""); setJarj({ avain: a.oletus, suunta: oletusSuunta }); }}>{t("muu.taulukko.tyhjenna")}</button>
          </span>
        )}
      </>
    );
  };

  return { rivit: jarjestetyt, haku, setHaku, jarj, Otsikko, Haku, piilotettu: kaikki.length - suodatetut.length };
}
